/**
 * Hero CTA analytics.
 *
 * One place for every interaction that starts in a page hero, so naming stays
 * consistent across the homepage, /masterclass and /advocacy:
 *
 *   hero_cta_click  — any button/link click inside a hero section
 *   hero_form_start — first meaningful interaction with a form the visitor
 *                     reached from a hero CTA (or a form rendered in a hero)
 *
 * Every event carries the hero id, the CTA id, the destination and the current
 * path, plus UTM attribution, so the funnel dashboard can join them.
 */
import { useCallback, useRef } from "react";
import { trackCTA, trackEvent } from "@/lib/tracking";
import { attributionProps } from "@/lib/utm";

type Props = Record<string, string | number | boolean | undefined>;

const ORIGIN_KEY = "hero_cta_origin";

export interface HeroOrigin {
  hero: string;
  cta_id: string;
  ts: number;
}

const base = (extra: Props = {}): Props => ({
  ...attributionProps(),
  ...extra,
});

/** Remembers which hero CTA sent the visitor onward, for form attribution. */
export function markHeroOrigin(hero: string, ctaId: string) {
  if (typeof window === "undefined") return;
  try {
    const payload: HeroOrigin = { hero, cta_id: ctaId, ts: Date.now() };
    window.sessionStorage.setItem(ORIGIN_KEY, JSON.stringify(payload));
  } catch {
    /* best-effort */
  }
}

/** Reads the last hero CTA the visitor clicked this session (if any). */
export function readHeroOrigin(): HeroOrigin | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(ORIGIN_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as HeroOrigin;
    return parsed && parsed.hero ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * A click on any CTA inside a hero section.
 * Fires `hero_cta_click` and the generic `cta_click` so existing CTA
 * dashboards keep working.
 */
export function trackHeroCTAClick(
  hero: string,
  ctaId: string,
  props: Props & { destination?: string } = {},
) {
  const payload = base({ hero, cta_id: ctaId, surface: hero, ...props });
  trackEvent("hero_cta_click", payload);
  trackCTA(ctaId, payload);
  markHeroOrigin(hero, ctaId);
}

/** Convenience wrapper for onClick handlers. */
export const onHeroCTA =
  (hero: string, ctaId: string, props?: Props & { destination?: string }) =>
  () =>
    trackHeroCTAClick(hero, ctaId, props);

/**
 * First interaction with a form. Attributes the start back to the hero CTA
 * that sent the visitor there when one was recorded this session.
 */
export function trackHeroFormStart(formId: string, props: Props = {}) {
  const origin = readHeroOrigin();
  trackEvent(
    "hero_form_start",
    base({
      form_id: formId,
      hero: origin?.hero ?? "none",
      cta_id: origin?.cta_id ?? "none",
      from_hero: Boolean(origin),
      ...props,
    }),
  );
}

/**
 * Returns a handler that fires `hero_form_start` exactly once per mount —
 * attach to a form's `onFocus` / `onChange` (both are safe, it de-dupes).
 */
export function useHeroFormStart(formId: string, props: Props = {}) {
  const fired = useRef(false);
  const propsRef = useRef(props);
  propsRef.current = props;

  return useCallback(() => {
    if (fired.current) return;
    fired.current = true;
    trackHeroFormStart(formId, propsRef.current);
  }, [formId]);
}
